import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Axios from 'axios';
import { FaCog } from 'react-icons/fa';
import intlShape from '@/shared/utils/intlShape';
import SimplePanel from '@/shared/components/SimplePanel';
import { FormattedMessage, injectIntl } from 'react-intl';
import moment from 'moment';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import api from '@/shared/api';
import config from '@/config';
import { showWhenPermission } from '@/shared/components/security';
import { injectStateService } from '@/shared/services/StateService';
import Button from '@/shared/components/Button';
import handleHttpError from '@/shared/services/HandleHttpError';
import { hasAssignments } from '@/shared/components/security/HasAssignments';
import CheckboxListModal from '@/shared/components/CheckboxListModal';
import userStorage from '@/shared/services/userStorage';
import SignalRService from '@/shared/services/SignalRService';
import ToastrService from '@/shared/services/ToastrService';
import ExportToExcelDropdown from '../registrationsPer/ExportToExcelDropdown';
import messages from '../reportingMessages';

import InvoicingHistoryFilters, { defaultFilters } from './InvoicingHistoryFilters';
import InvoicingHistoryVirtualTable from './InvoicingHistoryVirtualTable';

const SessionStorageKey = 'InvoicingHistoryReportParams';
const columnSelectionStorageKey = 'InvoicingHistoryReportHiddenColumns';

const columnDefinitions = [
  { id: 'relation', message: messages.Relation },
  { id: 'invoicingType', message: messages.InvoicingType },
  { id: 'lastInvoiceDate', message: messages.LastInvoiceDate },
  { id: 'nextScheduleDate', message: messages.NextScheduleDate },
  { id: 'nextScheduleAmount', message: messages.NextScheduleAmount },
  { id: 'fixedAmount', message: messages.FixedAmountOrAdvance },
  { id: 'draftInvoiceAmount', message: messages.DraftInvoiceAmount },
  { id: 'potential', message: messages.Potential },
  { id: 'toInvoice', message: messages.ToInvoice },
  { id: 'toInvoiceHours', message: messages.ToInvoiceHours },
  { id: 'avgAmountPerHour', message: messages.AvgAmountPerHour },
  { id: 'billedAmount', message: messages.BilledAmount },
  { id: 'billedHours', message: messages.BilledHours },
  { id: 'avgAmountPerHourBilled', message: messages.AvgAmountPerHourBilled },
  { id: 'months', message: messages.BilledPerMonth },
  { id: 'totalHoursAmount', message: messages.TotalHoursAmount },
  { id: 'writtenOffInternally', message: messages.WrittenOffAmount },
];

class InvoicingHistoryContainer extends Component {
  state = {
    reportFilters: {},
    rows: null,
    columns: null,
    totalAmount: 0,
    totalDraftAmount: 0,
    totalHours: 0,
    totalToInvoice: 0,
    totalToInvoiceHours: 0,
    totalPotential: 0,
    totalFixedAmount: 0,
    totalWrittenOffAmount: 0,
    showAssignments: false,
    showColumnSelectionModal: false,
    hideableColumns: [],
    exporting: false,
  };

  constructor(props) {
    super(props);
    const { searchParams = {}, intl } = props;
    const sessionParams = JSON.parse(sessionStorage.getItem(SessionStorageKey)) || {};

    if (sessionParams.dateFrom) {
      sessionParams.dateFrom = moment(sessionParams.dateFrom);
    }
    if (sessionParams.dateUntil) {
      sessionParams.dateUntil = moment(sessionParams.dateUntil);
    }
    const hiddenColumns = userStorage.getItem(columnSelectionStorageKey) || [];
    const hideableColumns = columnDefinitions.map(({ id, message }) => ({
      id,
      label: intl.formatMessage(message),
      selected: hiddenColumns.indexOf(id) === -1,
    }));
    const columnStatus = hideableColumns.reduce((o, col) => {
      // eslint-disable-next-line no-param-reassign
      o[col.id] = !!col.selected;
      return o;
    }, {});
    this.state = {
      ...this.state,
      reportFilters: {
        ...defaultFilters,
        ...sessionParams,
        ...searchParams,
      },
      hideableColumns,
      columnStatus,
    };
  }

  componentDidMount() {
    this.retrieve();
  }

  componentWillUnmount() {
    if (this.reportToken && !this.reportToken.token.reason) {
      this.reportToken.cancel('Operation canceled due to leaving page.');
    }
  }

  getParams() {
    const { reportFilters } = this.state;
    const {
      relationId,
      invoiceStreamId,
      relationTypes,
      ownCompanies,
      responsible,
      assistant,
      associate,
      assistantBackup,
      billingUser,
      seniorUser,
      sliding,
      dateFrom,
      dateUntil,
      withLabels,
      withoutLabels,
      teamCodes,
      invoiceScheduleTypes,
      assignmentTemplateId,
      relationGroups,
      sectors,
    } = reportFilters;

    return {
      relationId,
      invoiceStreamId,
      relationTypes,
      ownCompanies,
      responsible,
      assistant,
      associate,
      assistantBackup,
      billingUser,
      seniorUser,
      sliding,
      dateFrom,
      dateUntil,
      withLabels,
      withoutLabels,
      teamCodes,
      invoiceScheduleTypes,
      assignmentTemplateId,
      relationGroups,
      sectors,
    };
  }

  retrieve() {
    const { reportFilters } = this.state;

    const params = this.getParams();

    const sessionParams = JSON.stringify(reportFilters);
    sessionStorage.setItem(SessionStorageKey, sessionParams);

    if (this.reportToken && !this.reportToken.token.reason) {
      this.reportToken.cancel('Operation canceled due to new request.');
    }
    this.reportToken = Axios.CancelToken.source();
    this.setState({ rows: null });
    return api.base
      .post(`${config.appUrls.reporting}/api/invoicingHistoryOverview`, params, {
        cancelToken: this.reportToken.token,
      })
      .then(({ data }) => {
        const {
          rows = [],
          months = [],
          totalAmount = 0,
          totalDraftAmount = 0,
          totalHours = 0,
          totalToInvoice = 0,
          totalPotential = 0,
          totalToInvoiceHours = 0,
          totalFixedAmount = 0,
          totalHoursAmount = 0,
          totalWrittenOffAmount = 0,
        } = data || {};

        this.setState({
          rows,
          columns: months,
          totals: {
            totalAmount,
            totalDraftAmount,
            totalHours,
            totalToInvoice,
            totalPotential,
            totalToInvoiceHours,
            totalFixedAmount,
            totalHoursAmount,
            totalWrittenOffAmount,
          },

          showAssignments: hasAssignments() && rows.some(x => x.assignmentId),
        });
      })
      .catch(error => {
        if (!Axios.isCancel(error)) return error;
      });
  }

  createExcelReport = (isDetailsReport = false) => {
    this.setState({ exporting: true });
    const { intl } = this.props;

    const reportParams = { ...this.getParams(), isDetailsReport };
    const languageCode = intl.locale;

    const params = {
      language: languageCode,
      signalRConnectionId: SignalRService.getConnectionId(),
      report: 3,
      parameters: JSON.stringify(reportParams),
      isDetailsReport,
    };

    return api.base
      .post(`${config.appUrls.reporting}/api/export`, params)
      .then(() => {
        ToastrService.info(intl.formatMessage(messages.ExportToExcelStarted));
      })
      .catch(error => {
        handleHttpError(intl)(error);
      })
      .finally(() => {
        this.setState({ exporting: false });
      });
  };

  filtersChanged(filters) {
    this.setState(
      state => ({
        ...state,
        reportFilters: {
          ...state.reportFilters,
          ...filters,
        },
      }),
      () => this.retrieve(),
    );
  }

  handleColumnSelection = items => {
    let hiddenColumns = items.filter(x => !x.selected).map(x => x.id);
    // Clear selection if all are hidden or none are hidden
    if (hiddenColumns.length === items.length || hiddenColumns.length === 0) hiddenColumns = [];
    userStorage.setItem(columnSelectionStorageKey, hiddenColumns);

    const { hideableColumns } = this.state;

    const newHideableColumns = hideableColumns.map(x => ({
      ...x,
      selected: hiddenColumns.indexOf(x.id) === -1,
    }));
    const columnStatus = newHideableColumns.reduce((o, col) => {
      // eslint-disable-next-line no-param-reassign
      o[col.id] = !!col.selected;
      return o;
    }, {});

    this.setState({
      hideableColumns: newHideableColumns,
      columnStatus,
      showColumnSelectionModal: false,
    });
  };

  render() {
    const { intl } = this.props;
    const {
      reportFilters,
      rows,
      columns,
      totals,
      showAssignments,
      showColumnSelectionModal,
      hideableColumns,
      columnStatus,
      exporting,
    } = this.state;
    return (
      <div className="invoicing-history">
        <div className="fixed-filters">
          <SimplePanel
            article="5434790500114"
            title={intl.formatMessage(messages.InvoicingHistory)}
          >
            <div className="info-block container-fluid">
              <InvoicingHistoryFilters
                filters={reportFilters}
                onFilterChange={filters => this.filtersChanged(filters)}
              />
            </div>
          </SimplePanel>
          {rows ? (
            <div className="row btn-row">
              <div className="col-md-6 pull-right content-right">
                <Button
                  btnClassName="btn btn-sm btn-default margin10R"
                  onClick={() => this.setState({ showColumnSelectionModal: true })}
                >
                  <span className="margin5R">
                    <FaCog className="react-icon" />
                  </span>
                  <FormattedMessage {...messages.ColumnConfig} />
                </Button>
                {rows && rows.length > 0 && (
                  <ExportToExcelDropdown
                    exporting={exporting}
                    createExcel={this.createExcelReport}
                    intl={intl}
                  />
                )}
              </div>
            </div>
          ) : null}
        </div>

        {rows ? (
          <InvoicingHistoryVirtualTable
            data={rows}
            showAssignments={showAssignments}
            columnStatus={columnStatus}
            monthColumns={columns}
            totals={totals}
            intl={intl}
          />
        ) : (
          <LoadingAnimation />
        )}
        <CheckboxListModal
          title={intl.formatMessage(messages.SelectColumns)}
          items={hideableColumns}
          sortByLabel={false} // keep column order as defined
          onSave={this.handleColumnSelection}
          onClose={() => this.setState({ showColumnSelectionModal: false })}
          show={showColumnSelectionModal}
        />
      </div>
    );
  }
}

InvoicingHistoryContainer.propTypes = {
  intl: intlShape.isRequired,
  searchParams: PropTypes.shape({ relationId: PropTypes.string }),
};

InvoicingHistoryContainer.defaultProps = {
  searchParams: {},
};

export default showWhenPermission(
  'InvoiceDraft',
  'read',
)(injectStateService(injectIntl(InvoicingHistoryContainer)));
